import React, { useState, useEffect } from "react";
import { Sparkles, CheckCircle2, Circle, Compass, ListChecks } from "lucide-react";
import { getConciergePartners, createConciergeRequest, getMyConciergeRequests, getMyOnboarding, toggleOnboardingStep, getHomeTracker, toggleMilestone } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { SectionCard } from "./PortalShared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const StepList = ({ steps, onToggle, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {steps.map((s) => (
      <button key={s.id} onClick={() => onToggle(s.id)} data-testid="onboarding-step" className={`w-full flex items-center gap-2.5 p-3 rounded-lg border text-left transition-colors ${s.done ? "bg-[#faf7f2] border-[#f0ece5]" : "bg-white border-[#f0ece5] hover:border-[#b8935f]"}`}>
        {s.done ? <CheckCircle2 size={17} className="text-green-600 flex-shrink-0" /> : <Circle size={17} className="text-[#b8935f] flex-shrink-0" />}
        <span className={`text-[14px] ${s.done ? "text-[#8a8a8a] line-through" : "text-[#2b2b2b]"}`}>{s.label || s.step}</span>
      </button>
    ))}
  </div>
);

const Progress = ({ pct, testid = "services-progress" }) => (
  <div className="mb-4"><div className="flex justify-between text-[12px] text-[#a0a0a0] mb-1"><span>Progress</span><span data-testid={testid}>{pct}%</span></div>
    <div className="h-2.5 rounded-full bg-[#f0ece5] overflow-hidden"><div className="h-full bg-[#b8935f] transition-all duration-500" style={{ width: `${pct}%` }} /></div></div>
);

const PortalServices = ({ role }) => {
  const { toast } = useToast();
  const [ob, setOb] = useState(null);
  const [partners, setPartners] = useState([]);
  const [myReqs, setMyReqs] = useState([]);
  const [tracker, setTracker] = useState(null);
  const [svc, setSvc] = useState("");

  const load = () => {
    getMyOnboarding().then(setOb).catch(() => {});
    getConciergePartners().then(setPartners).catch(() => {});
    getMyConciergeRequests().then(setMyReqs).catch(() => {});
    if (role === "buyer") getHomeTracker().then(setTracker).catch(() => {});
  };
  useEffect(() => { load(); }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const flipStep = async (id) => { const u = await toggleOnboardingStep(id); setOb(u); };
  const flipMs = async (id) => { const u = await toggleMilestone(id); setTracker(u); };
  const request = async () => {
    if (!svc) return;
    try { await createConciergeRequest({ service: svc }); setSvc(""); getMyConciergeRequests().then(setMyReqs); toast({ title: "Concierge request sent", description: "Our team will be in touch." }); }
    catch { toast({ title: "Couldn't send request" }); }
  };

  return (
    <div className="space-y-6" data-testid="portal-services">
      <SectionCard title="Your Onboarding">
        {ob && <><Progress pct={ob.progress} testid="onboarding-progress" /><StepList steps={ob.steps} onToggle={flipStep} testid="onboarding-steps" /></>}
      </SectionCard>

      {role === "buyer" && tracker && (
        <SectionCard title="Home Buying Tracker">
          <Progress pct={tracker.progress} testid="tracker-progress" />
          <StepList steps={tracker.milestones} onToggle={flipMs} testid="tracker-milestones" />
        </SectionCard>
      )}

      <SectionCard title="Anadear Concierge">
        <p className="text-[13px] text-[#6a6a6a] mb-4 flex items-center gap-2"><Sparkles size={15} className="text-[#b8935f]" /> One call for removals, mortgages, conveyancing, cleaning, utilities & more.</p>
        <div className="flex gap-2 mb-4">
          <Select value={svc} onValueChange={setSvc}>
            <SelectTrigger className="h-[46px] border-[#e2d8c8]" data-testid="concierge-service-select"><SelectValue placeholder="Choose a service…" /></SelectTrigger>
            <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.service}>{p.service} — {p.name}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={request} data-testid="request-concierge-btn" className="px-5 rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors">Request</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {partners.map((p) => (
            <div key={p.id} className="p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]"><p className="text-[13px] font-500 text-[#2b2b2b]">{p.service}</p><p className="text-[12px] text-[#8a8a8a]">{p.name} — {p.desc}</p></div>
          ))}
        </div>
        {myReqs.length > 0 && (
          <div><p className="text-[13px] font-500 text-[#6a6a6a] mb-2 flex items-center gap-1.5"><ListChecks size={14} /> Your requests</p>
            {myReqs.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-[#f0ece5] mb-1" data-testid="my-concierge-row"><span className="text-[13px] text-[#2b2b2b]">{r.service}</span><span className="text-[11px] px-2 py-0.5 rounded-full bg-[#b8935f]/10 text-[#b8935f] capitalize">{r.status.replace("-", " ")}</span></div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default PortalServices;
